"use client";

import { getInnerQueryData } from "@nimble/react-requests/data/response-utils";
import { useUrlDrawer } from "@nimble/react-state-hooks/use-url-drawer";

import {
  ALERTS_DRAWER_OPEN_VALUE,
  ALERTS_DRAWER_PARAM,
} from "@/app/(discover)/jobs/[stateName]/(state-jobs)/components/alerts/alerts-drawer";
import {
  ALERTS_STATUS,
  AlertsButtonVariant,
  AlertsTriggerButton,
  AlertsTriggerButtonProps,
} from "@/app/(discover)/jobs/[stateName]/(state-jobs)/components/alerts/alerts-trigger-button.view";
import { isActiveFrequency } from "@/app/(discover)/jobs/[stateName]/(state-jobs)/components/alerts/notify-frequency";
import { withAuthGate } from "@/components/shared/auth-paywall/with-auth-gate";
import {
  GetJobAlertsResponse,
  useGetJobAlerts,
} from "@/hooks/data/use-job-alerts";
import { User } from "@/lib/types/auth";

type JobAlerts = NonNullable<GetJobAlertsResponse["data"]>;

export interface AlertsButtonProps {
  variant: AlertsButtonVariant;
  testId: string;
}

type AlertsTriggerButtonWithDrawerProps = Omit<
  AlertsTriggerButtonProps,
  "onClick"
>;

const countEnabledSources = (alerts: JobAlerts): number =>
  [
    alerts.preferencesEnabled,
    alerts.savedSchoolsEnabled,
    alerts.savedSearchesEnabled,
  ].filter(Boolean).length;

const AlertsTriggerButtonWithDrawer = ({
  variant,
  status,
  activeCount,
  testId,
}: AlertsTriggerButtonWithDrawerProps) => {
  const { openDrawer } = useUrlDrawer({ paramName: ALERTS_DRAWER_PARAM });

  return (
    <AlertsTriggerButton
      variant={variant}
      status={status}
      activeCount={activeCount}
      testId={testId}
      onClick={() => openDrawer(ALERTS_DRAWER_OPEN_VALUE)}
    />
  );
};

const AlertsButtonAuthenticated = ({
  variant,
  testId,
  user,
}: AlertsButtonProps & { user: User }) => {
  const jobAlertsQuery = useGetJobAlerts(user.id);

  if (jobAlertsQuery.isError) {
    return (
      <AlertsTriggerButtonWithDrawer
        variant={variant}
        status={ALERTS_STATUS.unavailable}
        activeCount={0}
        testId={testId}
      />
    );
  }

  const alerts = getInnerQueryData(jobAlertsQuery);

  if (!alerts || !isActiveFrequency(alerts.notifyFrequency)) {
    return (
      <AlertsTriggerButtonWithDrawer
        variant={variant}
        status={ALERTS_STATUS.off}
        activeCount={0}
        testId={testId}
      />
    );
  }

  return (
    <AlertsTriggerButtonWithDrawer
      variant={variant}
      status={ALERTS_STATUS.on}
      activeCount={countEnabledSources(alerts)}
      testId={testId}
    />
  );
};

export const AlertsButton = withAuthGate(AlertsButtonAuthenticated, {
  customLoader: null,
  customPaywall: (props) => (
    <AlertsTriggerButtonWithDrawer
      {...props}
      status={ALERTS_STATUS.off}
      activeCount={0}
    />
  ),
});
